/**
 * Deterministic generators for the procedural effect overlays — noise, texture,
 * and shader fields.
 *
 * The whole point of this module: the LIVE editor (DOM overlay divs) and the
 * PNG/PDF EXPORT (Canvas2D) must show pixel-identical noise/texture/shader, or
 * the export stops matching the design. Both call the SAME generator with a
 * FIXED seed, so both get byte-identical canvases. DOM consumes them as data
 * URLs (`*Url` getters); export draws the canvases directly.
 *
 * Everything is cached by its parameters so repeated renders are cheap.
 */

/* Seeded PRNG (mulberry32) — fixed seed everywhere, so DOM and export agree. */
function mulberry32(seed) {
    let a = seed >>> 0;
    return function next() {
        a |= 0;
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Parse #rgb / #rrggbb / #rrggbbaa / 'transparent' → [r,g,b,a(0..1)] */
export function parseColor(input, fallback = [0, 0, 0, 1]) {
    if (!input || input === 'transparent') return input === 'transparent' ? [0, 0, 0, 0] : fallback;
    let hex = String(input).trim();
    if (hex[0] !== '#') return fallback;
    hex = hex.slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    if (hex.length === 4) hex = hex.split('').map((c) => c + c).join('');
    const int = parseInt(hex.slice(0, 6), 16);
    if (!Number.isFinite(int)) return fallback;
    const a = hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255, a];
}

function rgba(input, alpha) {
    const [r, g, b, a] = parseColor(input);
    return `rgba(${r},${g},${b},${alpha != null ? alpha : a})`;
}

/* ------------------------------------------------------------------ *
 *  Cache — keyed by a stringified param signature. Values are canvases.
 * ------------------------------------------------------------------ */

const canvasCache = new Map();
const urlCache = new Map();

function cached(key, make) {
    let c = canvasCache.get(key);
    if (!c) {
        c = make();
        canvasCache.set(key, c);
    }
    return c;
}

function cachedUrl(key, canvas) {
    let u = urlCache.get(key);
    if (!u) {
        u = canvas.toDataURL('image/png');
        urlCache.set(key, u);
    }
    return u;
}

function newCanvas(w, h) {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
}

/* ------------------------------------------------------------------ *
 *  Noise — square, seamlessly-tiling grain. Alpha carries the grain;
 *  the caller scales strength with the overlay opacity + blend mode.
 * ------------------------------------------------------------------ */

export function makeNoiseTile({ size = 2, density = 100, type = 'mono' } = {}) {
    const s = clamp(Math.round(size) || 2, 1, 24);
    const d = clamp(density, 1, 100) / 100;
    const key = `noise|${s}|${d}|${type}`;
    return cached(key, () => {
        const cells = Math.max(6, Math.round(220 / s));
        // 1px-per-cell grid, then nearest-neighbour up-scale to blocky grain.
        const grid = newCanvas(cells, cells);
        const gctx = grid.getContext('2d');
        const img = gctx.createImageData(cells, cells);
        const rnd = mulberry32(0x9e3779b9);
        for (let i = 0; i < cells * cells; i += 1) {
            const gate = rnd();
            const o = i * 4;
            let r = 0; let g = 0; let b = 0; let a = 0;
            if (type === 'color') {
                const rr = rnd() * 255; const gg = rnd() * 255; const bb = rnd() * 255;
                if (gate < d) { r = rr; g = gg; b = bb; a = 255; }
            } else {
                const v = rnd() * 255;
                if (gate < d) { r = v; g = v; b = v; a = 255; }
            }
            img.data[o] = r; img.data[o + 1] = g; img.data[o + 2] = b; img.data[o + 3] = a;
        }
        gctx.putImageData(img, 0, 0);
        const tile = newCanvas(cells * s, cells * s);
        const tctx = tile.getContext('2d');
        tctx.imageSmoothingEnabled = false;
        tctx.drawImage(grid, 0, 0, cells, cells, 0, 0, cells * s, cells * s);
        return tile;
    });
}

export function noiseTileUrl(params) {
    const s = clamp(Math.round(params.size) || 2, 1, 24);
    const d = clamp(params.density ?? 100, 1, 100) / 100;
    const key = `noise|${s}|${d}|${params.type || 'mono'}`;
    return cachedUrl(key, makeNoiseTile(params));
}

/* ------------------------------------------------------------------ *
 *  Texture — seamlessly-tiling procedural pattern in a single colour.
 *  Marks are drawn at full alpha; overlay opacity/blend do the rest.
 * ------------------------------------------------------------------ */

const TEXTURE_TYPES = ['paper', 'grain', 'lines', 'cross', 'dots', 'grid'];

export function makeTextureTile(type, { scale = 6, color = '#000000' } = {}) {
    const u = clamp(Math.round(scale) || 6, 2, 64);
    const key = `tex|${type}|${u}|${color}`;
    return cached(key, () => {
        const [r, g, b] = parseColor(color);
        const col = (a) => `rgba(${r},${g},${b},${a})`;

        if (type === 'lines' || type === 'cross') {
            const c = newCanvas(u, u);
            const ctx = c.getContext('2d');
            ctx.strokeStyle = col(1);
            ctx.lineWidth = Math.max(1, u * 0.09);
            ctx.beginPath(); ctx.moveTo(0, u); ctx.lineTo(u, 0); ctx.stroke();
            if (type === 'cross') { ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(u, u); ctx.stroke(); }
            return c;
        }
        if (type === 'grid') {
            const c = newCanvas(u, u);
            const ctx = c.getContext('2d');
            ctx.strokeStyle = col(1);
            ctx.lineWidth = Math.max(1, u * 0.07);
            const lw = ctx.lineWidth;
            ctx.strokeRect(lw / 2, lw / 2, u - lw, u - lw);
            return c;
        }
        if (type === 'dots') {
            const c = newCanvas(u, u);
            const ctx = c.getContext('2d');
            ctx.fillStyle = col(1);
            ctx.beginPath();
            ctx.arc(u / 2, u / 2, Math.max(0.6, u * 0.18), 0, Math.PI * 2);
            ctx.fill();
            return c;
        }
        // paper / grain — random speckle field
        const cells = type === 'grain' ? 64 : 48;
        const c = newCanvas(cells, cells);
        const ctx = c.getContext('2d');
        const img = ctx.createImageData(cells, cells);
        const rnd = mulberry32(0x01234567);
        const density = type === 'grain' ? 0.5 : 0.2;
        for (let i = 0; i < cells * cells; i += 1) {
            const on = rnd() < density;
            const strength = rnd();
            const o = i * 4;
            const a = on ? (type === 'grain' ? strength * 0.9 + 0.1 : strength * 0.55) : 0;
            img.data[o] = r; img.data[o + 1] = g; img.data[o + 2] = b;
            img.data[o + 3] = Math.round(a * 255);
        }
        ctx.putImageData(img, 0, 0);
        return c;
    });
}

export function textureTileUrl(type, params) {
    const u = clamp(Math.round(params.scale) || 6, 2, 64);
    const key = `tex|${type}|${u}|${params.color || '#000000'}`;
    return cachedUrl(key, makeTextureTile(type, params));
}

export { TEXTURE_TYPES };

/* ------------------------------------------------------------------ *
 *  Shader — a full-element procedural gradient field. Generated at a
 *  capped resolution (aspect-preserving) and stretched to the element,
 *  so DOM (background-size:100% 100%) and export (drawImage stretch) match.
 * ------------------------------------------------------------------ */

const SHADER_TYPES = ['mesh', 'aurora', 'holographic', 'sunset'];

// Bucket sizes so tiny drags don't churn the cache / regenerate constantly.
function bucket(v) { return Math.max(2, Math.round(v / 8) * 8); }

export function makeShaderField(type, w, h, {
    color1 = '#ff5d8f', color2 = '#4d7cff', color3 = '#9d4dff', angle = 45, scale = 100,
} = {}) {
    const bw = bucket(w);
    const bh = bucket(h);
    const key = `shader|${type}|${bw}x${bh}|${color1}|${color2}|${color3}|${angle}|${scale}`;
    return cached(key, () => {
        const maxD = 512;
        const ratio = Math.min(1, maxD / Math.max(bw, bh));
        const cw = Math.max(2, Math.round(bw * ratio));
        const ch = Math.max(2, Math.round(bh * ratio));
        const c = newCanvas(cw, ch);
        const ctx = c.getContext('2d');
        const s = clamp(scale, 10, 200) / 100;

        if (type === 'mesh') {
            ctx.fillStyle = color1;
            ctx.fillRect(0, 0, cw, ch);
            const blobs = [
                [0.18, 0.22, color2], [0.82, 0.18, color3],
                [0.75, 0.82, color2], [0.15, 0.85, color3], [0.5, 0.5, color1],
            ];
            blobs.forEach(([fx, fy, col]) => {
                const rad = (Math.max(cw, ch) * 0.6) / s;
                const g = ctx.createRadialGradient(fx * cw, fy * ch, 0, fx * cw, fy * ch, rad);
                g.addColorStop(0, rgba(col, 0.9));
                g.addColorStop(1, rgba(col, 0));
                ctx.fillStyle = g;
                ctx.fillRect(0, 0, cw, ch);
            });
        } else if (type === 'holographic') {
            const cx = cw / 2; const cy = ch / 2;
            let g;
            if (typeof ctx.createConicGradient === 'function') {
                g = ctx.createConicGradient((angle * Math.PI) / 180, cx, cy);
            } else {
                g = ctx.createLinearGradient(0, 0, cw, ch);
            }
            const stops = [color1, color2, color3, color1, color2, color3, color1];
            stops.forEach((col, i) => g.addColorStop(i / (stops.length - 1), col));
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, cw, ch);
            // soft radial sheen from centre
            const sheen = ctx.createRadialGradient(cx * 0.7, cy * 0.6, 0, cx, cy, Math.max(cw, ch) * 0.7);
            sheen.addColorStop(0, 'rgba(255,255,255,0.28)');
            sheen.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = sheen;
            ctx.fillRect(0, 0, cw, ch);
        } else {
            // aurora / sunset — angled multi-stop linear gradient
            const rad = (angle * Math.PI) / 180;
            const cx = cw / 2; const cy = ch / 2;
            const len = (Math.sqrt(cw * cw + ch * ch) / 2) / s;
            const g = ctx.createLinearGradient(
                cx - Math.cos(rad) * len, cy - Math.sin(rad) * len,
                cx + Math.cos(rad) * len, cy + Math.sin(rad) * len,
            );
            if (type === 'aurora') {
                g.addColorStop(0, color1);
                g.addColorStop(0.4, color2);
                g.addColorStop(0.6, color3);
                g.addColorStop(1, color1);
            } else {
                g.addColorStop(0, color1);
                g.addColorStop(0.5, color2);
                g.addColorStop(1, color3);
            }
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, cw, ch);
        }
        return c;
    });
}

export function shaderFieldUrl(type, w, h, params) {
    const bw = bucket(w); const bh = bucket(h);
    const p = params || {};
    const key = `shader|${type}|${bw}x${bh}|${p.color1}|${p.color2}|${p.color3}|${p.angle}|${p.scale}`;
    return cachedUrl(key, makeShaderField(type, w, h, params));
}

export { SHADER_TYPES };
